<template>
  <div id="main" class="content" :ref="`main-content`">
    <section :ref="getBlockRef('ch1')" id="ch1" class="section section--h-auto">
      <div class="section__content">
        <h1 class="section__title">Ch. 1 <span class="section__title--after">Foreword</span></h1>
         <share-button :link="sharingLink('ch1')" />
        <div class="block" style="padding-top: 43px">
          <image-row :items="chapterOneImages" />
        </div>

        <div class="block b--column b--padding-large">

          <h2 class="section__sub-title">Introducing the Moda Operandi Runway Report</h2>

          <p class="p tac">
            Today we’re thrilled to introduce the first-ever Moda Operandi Runway Report, an analysis of Moda Operandi’s Trunkshow data that forecasts next season’s fashion trends, shopping behaviors, and geo patterns—as determined by the luxury fashion consumer.          </p>
          <p class="p tac">
            Why are Moda Operandi’s insights different? To understand, it’s worth exploring how styles typically become a trend. Each season, fashion designers showcase their collections on the runway—from New York to London to Milan to Paris— to industry insiders sitting front row.  It’s the buyers’ job to select which pieces—most often a fraction of the entire collection—they’ll ultimately make available to customers in their stores months later.    </p>

          <p class="p tac">
            Thanks in large part to social media, there is now a live conversation occurring as Fashion Month is happening: A collection walks the runway and you discover it on Instagram instantly. You post, you comment, you like...and thanks to Moda, you shop. We are the only retailer enabling customers to buy into that buzz in real time.          </p>
          <p class="p tac">
            With a Moda Trunkshow, customers shop from a designers’ complete collection within days of its debut, securing their look and size months before knowing whether it will be carried in stores. It satisfies that sense of immediacy so many have been looking for when they discover something on a social channel, and want to buy it right away.
          </p>
          <p class="p tac">
Why do these Trunkshow insights matter? Customers are “voting” with their clicks and dollars, telling Moda what pieces they want to wear next season and eliminating the guesswork as we make inventory decisions: We carry next season’s inventory based on what the customer has already told us she wants. In fact, last season we found styles that sold meaningfully in Trunkshow sold 10x more quantity in our in-season Boutique months later than those that didn’t.
          </p>
          <p class="p tac">
This model gives customers more of what they want, while allowing Moda to make increasingly sustainable inventory decisions. But more importantly, we find that our fashion-forward customer has a high degree of predictive power for what will eventually become a trend. It allows us to put her in the driver’s seat, where she, not only buyers, dictate what will be trending. Moda’s Runway Report offers a look behind the curtain, showing you what luxury consumers really want.
          </p>
          <p class="p tac">
We’re excited to share with you some of our findings.
<br /><br />
Until next season,
<br /><br />
Ganesh Srivats, CEO</p>
        </div>
      </div>
    </section>

    <section id="ch2" :ref="getBlockRef('ch2')" class="section">
      <div class="section__content">
        <h1 class="section__title">Ch. 2 <span class="section__title--after">The Findings</span></h1>
        <share-button :link="sharingLink('ch2')" />

        <div class="block b--column b--padding-large">
          <p class="p tac">
            Moda hosted 120+ Trunkshows this Spring season, inviting customers to explore the top collections—from buzzy names to heritage brands—and shop for their favorite looks. Who came out on top? Find those our customers loved the most (by city and according to their clicks) here.
          </p>
        </div>

        <div
          v-for="item in blocks"
          :key="item.name"
          class="block-mo"
          :ref="getBlockRef('ch2', item.name)"
          :id="`block-mo--${item.name}`"
        >
            <div
              @click="showModal(`LightBox`, item)"
              :ref="`block__header-${item.name}`"
              class="block-mo__header"
            >
              <span class="block-mo__header__title">{{ item.blockTitle || item.title }}</span>
              <img :src="item.image" />
              <ul class="block-mo__header__captions">
                <li :key="key" v-for="(caption, key) in item.captions">
                  <span class="bold">{{ caption.title }}</span>
                  <span>{{ caption.subTitle }}</span>
                </li>
              </ul>
            </div>
        </div>

        <div class="block b--column b--padding-large" :ref="getBlockRef('ch2', 'designers-on-the-rise')">
          <h2 class="section__sub-title">Fall’s Data-Driven Luxury Trends</h2>

          <p class="p tac">What will the luxury fashion consumer be wearing next season? There’s no better indicator than customers voting with their dollars, and they suggest these are the trends you’ll be seeing next fall.</p>
        </div>
        <ul ref="grid" class="grid grid--column grid--padding pt0">
          <li
            v-for="(row, key) in trends"
            :key="key"
            :id="key"
            :ref="getBlockRef('ch2', row.identifier)"
            class="grid__row">
            <share-button :link="sharingLink(`ch2/${row.identifier}`)" />

            <div
              v-for="(item, index) in row.items"
              :key="item.name"
              @click="selectItem(item)"
              :id="item.name"
              :class="[
                `grid__item`,
                index === 1 ? `grid__item--inverted` : ``,
                activeItem && activeItem === item ? `grid__item--active` : ``,
              ]">

              <img class="grid__image" :src="item.image" />
              <h3 class="grid__title" v-html="item.title"></h3>
              <h4 class="grid__sub-title">{{ item.subTitle }}</h4>
            </div>
          </li>
        </ul>

          <div class="block b--column b--padding-large" :ref="getBlockRef('ch2', 'the-top-colors')">
            <h2 class="section__sub-title">The Top Colors</h2>
            <p class="p tac">Which shades are in, which are out? Before you prep your fall wardrobe, brush up on the colors the luxury fashion consumer will be wearing next season, based on volume of Trunkshow orders. Hang on to all things <span class="c--green bold">GREEN</span>, <span class="c--grey bold">GREY</span>, <span class="c--white bold">WHITE</span>, <span class="c--neutral bold">NEUTRAL</span>, and <span class="c--pink bold">PINK</span>: They’ll be trending.  </p>
          </div>
        </div>
        <hero :image="topColorsImage" :share-link="sharingLink('ch2/the-top-colors')" />

    </section>

    <section id="ch3" :ref="getBlockRef('ch3')" class="section">
      <div class="section__content">
        <h1 class="section__title">Ch. 3 <span class="section__title--after">Digging Deeper</span></h1>
        <share-button :link="sharingLink('ch3')" />

        <div :key="info.identifier" :ref="getBlockRef('ch3', info.identifier)" class="block block--column" v-for="info in infographics">

          <div class="block block--column b--padding-large">
            <h2 class="section__sub-title">{{ info.title }}</h2>
            <p class="p tac">{{ info.description }} </p>
          </div>

          <div class="infographic block pt0 b--padding-large">
            <div class="i__graphic">
              <share-button :link="sharingLink(`ch3/${info.identifier}`)" />
              <img @click="showModal(`LightBox`, info)" :src="info.image" />
            </div>
          </div>
        </div>

        <div class="block block--column b--padding-large" :ref="getBlockRef('ch3', 'runway-obsessed-cities')">
          <h2 class="section__sub-title">The Most Runway-Obsessed Cities</h2>
          <p class="p tac">
            Which cities are most runway-obsessed? We looked at top FW ‘19 Trunkshow spenders across the country and around the world. Here are the cities spending the most on luxury fashion.
          </p>
        </div>

        <div class="block b--padding-large pt0">
          <div class="infographic i--us">
            <h3 class="i__title i__title--border tac">Top U.S.<br>Spending Cities</h3>

            <ul class="i__list i__list--us">
              <li class="list__item">New York, New York</li>
              <li class="list__item">Los Angeles,  California</li>
              <li class="list__item">Greenwich, Connecticut</li>
              <li class="list__item">Dallas, Texas</li>
              <li class="list__item">San Francisco, California</li>
              <li class="list__item">Houston, Texas</li>
              <li class="list__item">Chicago, Illinois</li>
              <li class="list__item">Washington, D.C.</li>
              <li class="list__item">Atlanta, Georgia</li>
              <li class="list__item">San Diego, California</li>
              <li class="list__item">Philadelphia, Pennsylvania</li>
              <li class="list__item">Austin, Texas</li>
              <li class="list__item">Paradise Valley, Arizona</li>
              <li class="list__item">Birmingham, Alabama</li>
              <li class="list__item">Shawnee Mission, Kansas</li>
            </ul>
          </div>

          <div class="infographic">
            <h3 class="i__title i__title--border tac">Top Global<br>Spending Cities</h3>

            <ul class="i__list i__list--global">
              <li class="list__item">London, United Kingdom</li>
              <li class="list__item">Hong Kong </li>
              <li class="list__item">Doha, Qatar</li>
              <li class="list__item">Riyadh, Saudi Arabia</li>
              <li class="list__item">Singapore</li>
              <li class="list__item">Paris, France</li>
              <li class="list__item">Kuwait City, Kuwait</li>
              <li class="list__item">Velden am Wörthersee, Austria</li>
              <li class="list__item">Toronto, Canada</li>
              <li class="list__item">Abu Dhabi, United Arab Emirates</li>
              <li class="list__item">Dubai, United Arab Emirates</li>
              <li class="list__item">Villefranche-Sur-Mer, France</li>
              <li class="list__item">Melle, Germany</li>
              <li class="list__item">Seoul, South Korea</li>
              <li class="list__item">Sydney, Australia</li>
            </ul>
          </div>
        </div>

        <div class="block b--column b--padding-large" :ref="getBlockRef('ch3', 'most-searched-fashion')">
          <h2 class="section__sub-title">Most Searched-For Luxury Fashion Terms</h2>
          <p class="p tac">We find that Trunkshow transactions are a strong indicator for what will be trending come next season. However, we also keep tabs on what people are searching for—and even what they’re not searching for. Here, we looked at what search terms were on the rise and on the decline, this Trunkshow season versus last.</p>
        </div>

        <hero :image="popularTermsImage" :share-link="sharingLink('ch3/most-searched-fashion')" />

        <div class="block b--column b--padding-large terms">
          <div
            v-for="(term, index) in popularTerms"
            :key="term.name"
            :class="[
              `term`,
              index % 2 !== 0 ? `term--inverted` : ``
            ]">

            <div class="term--column col1">
              <img class="term__image" :src="term.image" />
            </div>

            <div class="term--column col2">
              <span class="term__title term__title--pre heading">0{{index + 1}}</span>
              <h3 class="term__title heading">{{ term.title }}</h3>
            </div>
          </div>

        </div>
        <hero :image="decliningTermsImage" :ref="getBlockRef('ch3', 'declining-terms')" :share-link="sharingLink('ch3/declining-terms')" />

        <div class="block b--column b--padding-large">
         <div
            v-for="(term, index) in decliningTerms"
            :key="term.name"
            :class="[
              `term`,
              index % 2 !== 0 ? `term--inverted` : ``
            ]">

            <div class="term--column col1">
              <img class="term__image" :src="term.image" />
            </div>

            <div class="term--column col2">
              <span class="term__title term__title--pre heading">0{{index + 1}}</span>
              <h3 class="term__title heading">{{ term.title }}</h3>
            </div>
          </div>

        </div>

        <div :ref="getBlockRef('ch3', 'shopping-sessions')" class="block b--column b--padding-large">
          <h2 class="section__sub-title">Shopping Sessions, By the Numbers</h2>
          <p class="p tac">Do you prefer to shop in the morning? The evening? On the weekend? Here, we looked at when during fashion month our customers were most likely to squeeze in a fall wardrobe shopping session. We found the biggest spike in sessions Monday afternoons and Sunday mornings.</p>
        </div>

        <hero :image="shoppingSessionsImage" :share-link="sharingLink('ch3/shopping-sessions')" />

        <div :ref="getBlockRef('ch3', 'star-power')" class="block b--column b--padding-large">
          <h2 class="section__sub-title">Star Power</h2>

          <p class="p tac">We know which designers are driving consumers’ fall wardrobes, but who are the celebrities influencing their style? We looked at the power just one person can have in driving traffic to a specific look. Here, see how style icons such as Sarah Paulson (wearing Brandon Maxwell), Meghan Markle (wearing Victoria Beckham), Lady Gaga (wearing Brandon Maxwell), and Lupita Nyong’o (wearing Oscar de la Renta) can generate interest and drive sales.</p>
        </div>

        <hero :image="starPowerImage" :share-link="sharingLink('ch3/star-power')" />
      </div>
    </section>

    <section class="block block--column block-mo__credits block--padding-large">
      <p class="credits__title"><strong>Contributing Analysts</strong></p>
      <p class="credits__description">Neha Dayal, Senior Analyst<br />Alicia Jetton, Analyst</p>
    </section>

    <a class="footer--logo" href="https://www.modaoperandi.com/women" />
  </div>
</template>

<script>
// Vuex
import { mapGetters, mapActions } from 'vuex'
// import Parallax from 'vue-parallaxy';
import ImageRow from '@/components/ui/ImageRow'
import ShareButton from '@/components/ui/ShareButton'
import Hero from '@/components/ui/Hero'

import navigation from '@/models/navigation'
import trends from '@/models/trends'
import blocks from '@/models/blocks'
import infographics from '@/models/infographics'
import { popular as popularTerms, declining as decliningTerms } from '@/models/terms'
import { setTimeout } from 'timers'

export default {
  name: `view-page`,

  // Components
  components: {
    // Parallax,
    ImageRow,
    ShareButton,
    Hero
  },

  // Data
  data: () => ({
    scrollThrottle: false, // Scroll throttle is set to optimize on scroll performance
    cancelScroll: null,
    lazyScrollHandler: null,
    onTheRiseImage: require('@/assets/chapters/ch2/designers-on-the-rise.png'),
    topColorsImage: require('@/assets/chapters/ch2/infographic_top_colors.gif'),
    popularTermsImage: require('@/assets/chapters/ch3/infographic_terms_increasing.gif'),
    decliningTermsImage: require('@/assets/chapters/ch3/infographic_terms_declining.gif'),
    shoppingSessionsImage: require('@/assets/chapters/ch3/infographic_shopping_sessions.png'),
    starPowerImage: require('@/assets/chapters/ch3/infographic_star_power.png'),
    infographics
  }),

  // Computed
  computed: {
    ...mapGetters(`page`, [`activeBlock`, `activeItem`]),
    ...mapGetters(`navigation`, [`activeMenuItem`, `activeChapter`]),

    blocks () {
      return blocks
    },

    trends () {
      return trends
    },

    popularTerms () {
      return popularTerms
    },
    decliningTerms () {
      return decliningTerms
    },
    chapterOneImages: () => [
      require('../assets/chapters/ch1/foreword_1.jpg'),
      require('../assets/chapters/ch1/foreword_2.jpg'),
      require('../assets/chapters/ch1/foreword_3.jpg'),
      require('../assets/chapters/ch1/foreword_4.jpg')
    ]
  },

  // Methods
  methods: {
    ...mapActions(`header`, [`toggleHeader`]),
    ...mapActions(`navigation`, [`toggleNavigation`, `setActiveMenuItem`, `setActiveChapter`]),
    ...mapActions(`sidebar`, [`toggleSidebar`]),
    ...mapActions(`page`, [`setActiveBlock`, `setActiveItem`]),
    ...mapActions(`modal`, [`setModalComponentName`, `toggleModal`, `setModalContent`]),

    showModal (name, content, sharingLink) {
      this.setModalComponentName(name)
      this.setModalContent(
        {
          img: content.fullResImage,
          desc: content.modalDesc,
          sharingLink
        }
      )
      this.toggleModal(true)
    },

    getBlockRef (chapter, block) {
      return ['block', chapter, block].filter(v => v !== undefined).join('-')
    },

    showBlockContent (chapter, block) {
      this.setActiveMenuItem(block)
      this.setActiveChapter(chapter)
      this.$router.push(`/page/${chapter}/${block}`)
    },

    getContentElement (block) {
      const elements = block.querySelectorAll('.block-mo__content')

      if (!elements || elements.length <= 0) {
        return null
      }

      return elements[0]
    },

    selectItem (item) {
      if (!item) return
      this.setActiveItem(item)
      this.setModalComponentName(`LightBox`)
      this.setModalContent(
        {
          img: item.fullResImage,
          desc: item.modalDesc || item.description
        }
      )
      this.toggleModal(true)
    },

    getActiveRouteElement () {
      return this.$refs[this.getBlockRef(this.$route.params.chapter, this.$route.params.block)][0]
    },

    moveToBlock (blockName) {
      let block = this.$refs[blockName]

      if (block.length) {
        block = block[0]
      }

      // First cancel previous scroll
      if (this.cancelScroll) {
        this.cancelScroll()
        this.cancelScroll = null
      }

      this.$scrollTo(block)

      /**
      let timeout = 0
      if (this.activeBlock) {
        const activeBlockElement = this.$refs[this.activeBlock]
        const contentElement = this.getContentElement(activeBlockElement)

        // Make sure to collapse the previous element, so scrollto goes to the right point in page
        if (contentElement) {
          const activeRect = activeBlockElement.getBoundingClientRect()
          const nextRect = block.getBoundingClientRect()
          if (nextRect.top > activeRect.top) {
            this.$scrollTo(activeBlockElement, 550)
            timeout = 550
          }

          this.collapseBlock(activeBlockElement, 0.550)
        }
      }
      setTimeout(() => {
        this.$scrollTo(block, 550)
        this.extendBlock(block)
      }, timeout)
      */
    },

    scrollHandler () {
      const results = navigation.reduce(function (prev, { identifier }) {
        const element = this.$refs[this.getBlockRef(identifier)]
        if (element) {
          const rect = element.getBoundingClientRect()
          if ((rect.top + rect.height > 0) && (rect.top < window.scrollY + window.innerHeight)) {
            return [...prev, {
              position: rect.top,
              identifier
            }]
          }
        }

        return prev
      }.bind(this), [])

      const { identifier } = results.reduce((min, item) => {
        if (min.position > item.position) {
          return item
        }
        return min
      }, results[0])

      if (identifier && identifier !== this.activeChapter && this.cancelScroll === null) {
        this.setActiveChapter(identifier)
        // this.$router.push(`/page/${identifier}/`)
      }
    },
    sharingLink (path) {
      return `${window.location.protocol}//${window.location.host}/page/${path}`
    }
  },

  // Watchers
  watch: {
    '$route.path': function (path) {
      if (path.startsWith('/page/')) {
        const blockName = this.getBlockRef(this.$route.params.chapter, this.$route.params.block)
        this.moveToBlock(blockName)
        this.setActiveBlock(blockName)

        this.setActiveMenuItem(this.$route.params.block)
        this.setActiveChapter(this.$route.params.chapter)
      }
    }
  },

  // Lifecycles
  mounted () {
    this.toggleHeader(true)
    this.toggleNavigation(true)
    this.toggleSidebar(true)
    const mainContent = this.$refs['main-content']
    this.lazyScrollHandler = _.debounce(this.scrollHandler, 300)
    mainContent.addEventListener('scroll', this.lazyScrollHandler)

    if (this.$route.params.chapter || this.$route.params.block) {
      setTimeout(() => {
        const blockName = this.getBlockRef(this.$route.params.chapter, this.$route.params.block)
        this.moveToBlock(blockName)
        this.setActiveChapter(this.$route.params.chapter)
        this.setActiveMenuItem(this.$route.params.block)
      }, 300)
    }
  },

  beforeDestroy () {
    const mainContent = this.$refs['main-content']
    this.lazyScrollHandler.cancel()
    mainContent.removeEventListener('scroll', this.lazyScrollHandler)
  }

  // Filters
}
</script>
